import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import classNames from "classnames";
import { useGetStatesQuery } from "@/modules/shared/store/services/endpoints/get-states";

const ITEM_TEMPLATE =
  "<div class='flex justify-between w-full'>" +
  "<span class='font-semibold'>${text}</span>" +
  "<span class='text-gray-500 dark:text-gray-400'>${code}</span>" +
  "</div>";

/**
 * StateSelect — Syncfusion DropDownList backed by RTK Query (getStates).
 *
 * Accepts a `countryId` prop; falls back to the current user's company country.
 * The RTK Query bridge populates `state.states` so legacy consumers keep working.
 */
export default function StateSelect({
  placeholder = "State *",
  value,
  onChange,
  countryId,
  error,
  disabled = false,
  className,
  floatLabelType = "Auto",
  ...props
}) {
  const { currentCompany: { countryId: defaultCountryId } } = useSelector(state => state.currentUser);
  const resolvedId = countryId || defaultCountryId;
  const { data: records, isFetching } = useGetStatesQuery(resolvedId, { skip: !resolvedId });

  const dataSource = useMemo(() => {
    if (!records?.length) return [];
    return records.map(s => ({ text: s.name, value: s.parameterId, code: s.code }));
  }, [records]);

  const handleChange = useCallback(
    e => onChange?.(e.itemData?.value ?? undefined),
    [onChange],
  );

  return (
    <div className={classNames("select-input-wrapper", className)}>
      <DropDownListComponent
        placeholder={placeholder}
        floatLabelType={floatLabelType}
        dataSource={dataSource}
        fields={{ text: "text", value: "value" }}
        value={value}
        change={handleChange}
        enabled={!disabled && !isFetching}
        cssClass={classNames("e-outline", { "e-error": !!error })}
        itemTemplate={ITEM_TEMPLATE}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

StateSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  /** Country ID — triggers RTK Query fetch; falls back to company country */
  countryId: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  floatLabelType: PropTypes.oneOf(["Auto", "Always", "Never"]),
};

